import React, { useState, useRef, useEffect } from "react";
import { Col, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { addDirectDepositInfo } from "../../redux/thunks/userThunks";
import UserProfileInput from "./UserProfileInput";
import { Toast } from "primereact/toast";
import UserProfileCountry from "./UserProfileCountry";
import "./SettingsAccountNewAccount.css";
import { useTranslation } from "react-i18next";
import UserProfileCurrency from "./UserProfileCurrency";
import { useNavigate } from "react-router-dom";

const SettingsAccountNewAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountName: "",
    bankName: "",
    transitNumber: "",
    branchNumber: "",
    accountNumber: "",
    email: "",
    currency: null,
    country: null,
  });

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const toast = useRef(null);

  const loginUser = useSelector((state) => state.user.user.data);
  const accountAddress = loginUser?.address || "";

  useEffect(() => {
    if (loginUser?.email) {
      setFormData((prev) => ({ ...prev, email: loginUser.email }));
    }
  }, [loginUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    console.log("formData updated:", formData);
  }, [formData]);

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const temp = {};
    if (!formData.accountName) temp.accountName = "Required";
    if (!formData.bankName) temp.bankName = "Required";
    if (!formData.transitNumber) temp.transitNumber = "Required";
    if (!formData.branchNumber) temp.branchNumber = "Required";
    if (!formData.accountNumber) temp.accountNumber = "Required";
    if (!formData.currency) temp.currency = "Required";
    if (!formData.country) temp.country = "Required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      toast.current?.show({
        severity: "error",
        summary: "Stripe error",
        detail: "Stripe is not initialized",
      });
      return;
    }

    if (!validate()) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fix form errors",
      });
      return;
    }

    try {
      await dispatch(
        addDirectDepositInfo(
          formData,
          loginUser.account_enable,
          stripe,
          loginUser
        )
      );

      setFormData({
        accountName: "",
        bankName: "",
        transitNumber: "",
        branchNumber: "",
        accountNumber: "",
        email: loginUser.email || "",
        currency: "",
        country: "",
      });

      setErrors({});

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Deposit account added successfully!",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message || "Failed to add account",
      });
    }
  };

  return (
    <div className="col-xl-10">
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">{t("add.deposite.account")}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="personal_validate">
            <div className="form-row">
              <UserProfileInput
                fieldLabel={t("accountHolder.name.label")}
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
              />
              {errors.accountName && (
                <small className="text-danger">{errors.accountName}</small>
              )}

              <UserProfileInput
                fieldLabel={t("bank.name.label")}
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
              />
              {errors.bankName && (
                <small className="text-danger">{errors.bankName}</small>
              )}

              <div className="form-group">
                <UserProfileCurrency
                  fieldLabel={t("currency.label")}
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                />
                {errors.currency && (
                  <small className="text-danger">{errors.currency}</small>
                )}
              </div>
              {errors.currency && (
                <small className="text-danger">{errors.currency}</small>
              )}
              <div className="form-group">
                <UserProfileCountry
                  fieldLabel={t("country.label")}
                  value={formData.country}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      country: e.target.value,
                    }))
                  }
                />
                {errors.country && (
                  <small className="text-danger">{errors.country}</small>
                )}
              </div>

              {errors.country && (
                <small className="text-danger">{errors.country}</small>
              )}

              <Row className="align-items-center">
                <Col xs={7}>
                  <UserProfileInput
                    fieldLabel={t("transit.number.label")}
                    name="branchNumber"
                    value={formData.branchNumber}
                    onChange={handleNumericChange}
                  />
                  {errors.branchNumber && (
                    <small className="text-danger">{errors.branchNumber}</small>
                  )}
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col xs={7}>
                  <UserProfileInput
                    fieldLabel={t("institution.number.label")}
                    name="transitNumber"
                    value={formData.transitNumber}
                    onChange={handleNumericChange}
                  />
                  {errors.transitNumber && (
                    <small className="text-danger">
                      {errors.transitNumber}
                    </small>
                  )}
                </Col>
              </Row>

              <UserProfileInput
                fieldLabel={t("account.number.label")}
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleNumericChange}
              />
              {errors.accountNumber && (
                <small className="text-danger">{errors.accountNumber}</small>
              )}

              <div className="form-group col-12 mt-3">
                {accountAddress !== "" ? (
                  <button type="submit" className="btn btn-success ps-5 pe-5">
                    {t("add.label")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate("/settings")}
                  >
                    {t("setup.before.add.banking.label")}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  );
};

export default SettingsAccountNewAccount;
